import React, { useId, useState } from 'react';
import { scaleForDisplay } from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import type { SummaryEntityCategory } from '../../../redux/slices/InsightsSlice';
import { SPEND_CATEGORY_COLORS } from './SpendPieChart';

/* ── Layout constants ────────────────────────────────────────────────────────
   154 × 170 viewBox.  Scale tick labels sit at the top (90°, liquid) and
   bottom (270°, mass) poles instead of the left/right equators, so no
   horizontal margin is needed for text — R_OUTER can be maximised.
   ─────────────────────────────────────────────────────────────────────────── */

const VIEW_W = 154;
const VIEW_H = 170;
const CX = 77;
const CY = 85;
/** Zero-line radius — bars grow outward from here. */
const R_INNER = 26;
/** Maximum bar radius. */
const R_OUTER = 72;
/**
 * Angular gap (in radians) between the two halves at the top and bottom
 * poles, so the mass/liquid sides are visually separated.
 */
const HALF_GAP_RAD = (4 / 180) * Math.PI;

/* ── Geometry helpers ────────────────────────────────────────────────────────
   Same polarToXY convention as NutritionProgressChart / SpendPieChart:
   angle 0 → 12 o'clock, increasing clockwise.
   ─────────────────────────────────────────────────────────────────────────── */

function polarToXY(
  cx: number,
  cy: number,
  r: number,
  angleRad: number,
): { x: number; y: number } {
  return {
    x: cx + r * Math.cos(angleRad - Math.PI / 2),
    y: cy + r * Math.sin(angleRad - Math.PI / 2),
  };
}

/**
 * SVG path for a radial bar — a filled donut sector from innerR to outerR
 * spanning [startAngle, endAngle] in the SVG angle convention.
 */
function barPath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const sweep = Math.max(endAngle - startAngle, 0.002);
  const end = startAngle + sweep;
  const o1 = polarToXY(cx, cy, outerR, startAngle);
  const o2 = polarToXY(cx, cy, outerR, end);
  const i1 = polarToXY(cx, cy, innerR, end);
  const i2 = polarToXY(cx, cy, innerR, startAngle);
  const large = sweep > Math.PI ? 1 : 0;
  const f = (n: number) => n.toFixed(3);
  return [
    `M ${f(o1.x)} ${f(o1.y)}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${f(o2.x)} ${f(o2.y)}`,
    `L ${f(i1.x)} ${f(i1.y)}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${f(i2.x)} ${f(i2.y)}`,
    'Z',
  ].join(' ');
}

/* ── Bar data ────────────────────────────────────────────────────────────────
   halfStart: the angular starting point of this half (incl. the HALF_GAP_RAD
   offset, e.g. HALF_GAP_RAD for the right/liquid half).
   ─────────────────────────────────────────────────────────────────────────── */

export interface QuantityProgressItem {
  key: string;
  label: string;
  category: SummaryEntityCategory;
  unit: 'kg' | 'L';
  /** Plan quantity in the normalised unit. 0 means no plan for this product. */
  plan: number;
  /** Actual quantity in the normalised unit. 0 means nothing applied yet. */
  actual: number;
}

interface BarDatum {
  item: QuantityProgressItem;
  startAngle: number;
  endAngle: number;
  /** Outer radius of the Actual bar, or null when Actual is 0 (nothing drawn). */
  actualOuterR: number | null;
  /** Radius of the Plan reference tick, or null when Plan is 0 (nothing drawn). */
  planTickR: number | null;
  ratio: number | null;
  isOver: boolean;
  color: string;
}

function buildBars(
  items: QuantityProgressItem[],
  halfStart: number,
  maxQ: number,
): BarDatum[] {
  const n = items.length;
  if (n === 0 || maxQ <= 0) return [];

  const span = Math.PI - 2 * HALF_GAP_RAD;
  const barWidth = span / n;
  // Angular gap between adjacent bars — smaller fraction for many items so
  // thin bars don't disappear entirely.
  const gap = Math.min(barWidth * 0.1, (2 / 180) * Math.PI);
  const net = Math.max(barWidth - gap, 0.002);

  return items.map((item, pos) => {
    const center = halfStart + (pos + 0.5) * barWidth;
    const ratio = item.plan > 0 ? item.actual / item.plan : item.actual > 0 ? Infinity : null;
    const isOver = ratio !== null && ratio > 1;
    const actualLen = (item.actual / maxQ) * (R_OUTER - R_INNER);
    return {
      item,
      startAngle: center - net / 2,
      endAngle: center + net / 2,
      actualOuterR: item.actual > 0 ? R_INNER + Math.max(actualLen, 1.5) : null,
      planTickR: item.plan > 0 ? R_INNER + (item.plan / maxQ) * (R_OUTER - R_INNER) : null,
      ratio,
      isOver,
      color: SPEND_CATEGORY_COLORS[item.category] ?? '#9ca3af',
    };
  });
}

/* ── Format helpers ──────────────────────────────────────────────────────────*/

function formatQty(quantity: number, unit: 'kg' | 'L'): string {
  const { value, unit: du } = scaleForDisplay(quantity, unit);
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${du}`;
}

/* ── Component ───────────────────────────────────────────────────────────────
   Angular layout:
     Right half (liquid, L)  — SVG angles [HALF_GAP_RAD, π − HALF_GAP_RAD]
                                midpoint: π/2 (3 o'clock)
     Left half  (mass, kg/t) — SVG angles [π + HALF_GAP_RAD, 2π − HALF_GAP_RAD]
                                midpoint: 3π/2 (9 o'clock)

   Items within each half are sorted alphabetically and laid out in order
   from the top pole clockwise around to the bottom pole. Each half shares one
   linear scale — maxQ = the largest Plan or Actual value present in that
   half — so a product exceeding its own plan simply draws a longer bar than
   its neighbours rather than needing any special-case handling.
   ─────────────────────────────────────────────────────────────────────────── */

interface CircularQuantityChartProps {
  items: QuantityProgressItem[];
  /** Controlled hover — lets a table row above the chart drive the same highlight as hovering a bar directly. Falls back to internal state when omitted. */
  hoveredKey?: string | null;
  onHoverKeyChange?: (key: string | null) => void;
}

const CircularQuantityChart: React.FC<CircularQuantityChartProps> = ({
  items,
  hoveredKey,
  onHoverKeyChange,
}) => {
  const uid = useId();
  const [internalHoverKey, setInternalHoverKey] = useState<string | null>(
    null
  );
  const hoverKey = hoveredKey !== undefined ? hoveredKey : internalHoverKey;
  const setHoverKey = onHoverKeyChange ?? setInternalHoverKey;

  const massLines = [...items.filter((l) => l.unit === 'kg')].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
  const liquidLines = [...items.filter((l) => l.unit === 'L')].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );

  if (massLines.length === 0 && liquidLines.length === 0) return null;

  const maxOf = (l: QuantityProgressItem[]) =>
    l.reduce((m, i) => Math.max(m, i.plan, i.actual), 0);
  const maxMass = maxOf(massLines);
  const maxLiquid = maxOf(liquidLines);

  // Right half = liquid, left half = mass
  const liquidBars = buildBars(liquidLines, HALF_GAP_RAD, maxLiquid);
  const massBars = buildBars(massLines, Math.PI + HALF_GAP_RAD, maxMass);
  const allBars = [...liquidBars, ...massBars];

  const hovered = allBars.find((b) => b.item.key === hoverKey) ?? null;

  // Pole Y positions for tick labels (top = liquid, bottom = mass)
  const topPoleY = CY - R_OUTER;    // 12 o'clock — right/liquid half label
  const bottomPoleY = CY + R_OUTER; // 6  o'clock — left/mass  half label

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full"
      role="img"
      aria-label="Quantity required plan vs actual chart"
    >
      {/* Inner baseline ring */}
      <circle
        cx={CX}
        cy={CY}
        r={R_INNER}
        fill="none"
        stroke="var(--color-secondary-light)"
        strokeWidth={0.6}
      />

      {/* Plan track — pale wedge (this product's own category colour) from the inner ring to its Plan-scaled radius, only where a Plan exists. Drawn under the Actual bar so any unfilled portion stays visible as the pale "remaining" track — a thin stroked tick line was tried first but read as barely visible against a white background. */}
      {allBars.map(
        (b) =>
          b.planTickR !== null && (
            <path
              key={`plan-${b.item.key}`}
              d={barPath(CX, CY, R_INNER, b.planTickR, b.startAngle, b.endAngle)}
              fill={b.color}
              opacity={!hoverKey || hoverKey === b.item.key ? 0.22 : 0.08}
              pointerEvents="none"
              className="transition-opacity duration-150"
            />
          )
      )}

      {/* Actual bars — only where something has been applied. */}
      {allBars.map(
        (b) =>
          b.actualOuterR !== null && (
            <path
              key={`bar-${b.item.key}`}
              d={barPath(CX, CY, R_INNER, b.actualOuterR, b.startAngle, b.endAngle)}
              fill={b.isOver ? 'var(--color-danger)' : b.color}
              opacity={!hoverKey || hoverKey === b.item.key ? 1 : 0.22}
              stroke="white"
              strokeWidth={0.3}
              pointerEvents="none"
              className="transition-opacity duration-150"
            />
          )
      )}

      {/* Invisible full-length hit area per bar — hover works even for plan-only products. */}
      {allBars.map((b) => (
        <path
          key={`hit-${b.item.key}`}
          d={barPath(CX, CY, R_INNER, R_OUTER, b.startAngle, b.endAngle)}
          fill="transparent"
          onMouseEnter={() => setHoverKey(b.item.key)}
          onMouseLeave={() => setHoverKey(null)}
          className="cursor-pointer"
        />
      ))}

      {/* ── Axis tick + scale label: top pole (90° / liquid) ── */}
      {liquidLines.length > 0 ? (
        <>
          <line
            x1={CX - 3}
            y1={topPoleY}
            x2={CX + 3}
            y2={topPoleY}
            stroke="var(--color-secondary-dark)"
            strokeWidth={0.8}
          />
        </>
      ) : (
        <text
          x={CX + 6}
          y={topPoleY - 3}
          textAnchor="start"
          dominantBaseline="auto"
          fontSize={7}
          fill="var(--color-secondary-dark)"
        >
          No liquid
        </text>
      )}

      {/* ── Axis tick + scale label: bottom pole (270° / mass) ── */}
      {massLines.length > 0 ? (
        <line
          x1={CX - 3}
          y1={bottomPoleY}
          x2={CX + 3}
          y2={bottomPoleY}
          stroke="var(--color-secondary-dark)"
          strokeWidth={0.8}
        />
      ) : (
        <text
          x={CX - 6}
          y={bottomPoleY + 9}
          textAnchor="end"
          dominantBaseline="auto"
          fontSize={7}
          fill="var(--color-secondary-dark)"
        >
          No solid
        </text>
      )}

      {/* ── Centre display ── */}
      {hovered ? (
        <>
          <text
            key={`hover-label-${uid}`}
            x={CX}
            y={CY - 8}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={7}
            fontWeight="600"
            fill="var(--color-charcoal)"
          >
            {hovered.item.label}
          </text>
          <text
            key={`hover-qty-${uid}`}
            x={CX}
            y={CY + 7}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={7}
            fontWeight="600"
            fill={hovered.isOver ? 'var(--color-danger)' : 'var(--color-charcoal)'}
          >
            {hovered.item.plan <= 0
              ? `${formatQty(hovered.item.actual, hovered.item.unit)} (unplanned)`
              : hovered.item.actual <= 0
                ? `0 / ${formatQty(hovered.item.plan, hovered.item.unit)}`
                : `${formatQty(hovered.item.actual, hovered.item.unit)} / ${formatQty(hovered.item.plan, hovered.item.unit)}`}
          </text>
        </>
      ) : (
        <>
          <text
            x={CX}
            y={CY - 5}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={7}
            fill="var(--color-secondary-dark)"
          >
            Required
          </text>
          <text
            x={CX}
            y={CY + 8}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={7}
            fill="var(--color-secondary-dark)"
          >
            Solid | Liquid
          </text>
        </>
      )}
    </svg>
  );
};

export default CircularQuantityChart;
